/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useMemo, useState } from 'react'

import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import type { AdminCustomerDetail } from './customerTypes'
import type { PermissionsCap } from './types'

const log = logger({ category: 'useCompanyProfile' })

export interface CompanyProfileState {
  cap: PermissionsCap | null
  detail: AdminCustomerDetail | null
  isLoading: boolean
  error: string
}

/**
 * The company's permission profile, as the cap the editor applies. A company the
 * customer table does not know (404) or one never configured (updatedAt null) caps
 * nothing; the name still comes from the company list so tooltips read well.
 */
export const useCompanyProfile = (companyId: number | undefined | null): CompanyProfileState => {
  const { api } = useApi()
  const [detail, setDetail] = useState<AdminCustomerDetail | null>(null)
  const [loadedFor, setLoadedFor] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (companyId === undefined || companyId === null) {
      setDetail(null)
      setLoadedFor(null)
      setError('')
      return
    }
    let cancelled = false
    setIsLoading(true)
    const load = async () => {
      try {
        const response = await api.get<AdminCustomerDetail>({
          path: `/app/admin/customers/${encodeURIComponent(String(companyId))}`,
          origin: 'useCompanyProfile',
        })
        if (cancelled) return
        if (response.error || !response.data) {
          // Not in the customer table yet: nothing to cap. Anything else is worth a line.
          setDetail(null)
          setError(response.code === 404 ? '' : response.message || 'Could not load the company profile')
        } else {
          setDetail(response.data)
          setError('')
        }
        setLoadedFor(companyId)
      } catch (ex) {
        log.error(ex)
        if (!cancelled) {
          setDetail(null)
          setError('Could not load the company profile')
          setLoadedFor(companyId)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [api, companyId])

  const cap = useMemo<PermissionsCap | null>(() => {
    if (companyId === undefined || companyId === null || loadedFor !== companyId) return null
    const companyName = detail?.customer.name ?? `company #${String(companyId)}`
    if (!detail || detail.profile.updatedAt === null) {
      return { grants: [], insights: [], hasProfile: false, companyName }
    }
    return { grants: detail.profile.grants, insights: detail.profile.insights, hasProfile: true, companyName }
  }, [companyId, detail, loadedFor])

  return { cap, detail, isLoading, error }
}

export default useCompanyProfile
